import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Select, { components } from "react-select";
import { AsyncPaginate, LoadOptions } from "react-select-async-paginate";
import SectionHeader from "components/SectionPillPropsIcons";
import { fetchMarketplaceProfilesSelect2 } from "api/postTypeApiCalls";
import {
  fetchBrokerageAgentsByOfficeIdsSelect2,
  fetchOfficeSelect,
  fetchOfficeTeamMembersSelect,
  resolveBrokerageOrganizationId,
  sessionLicenseFromAuthCookies,
} from "api/installationApi";
import { showError, showSuccess } from "toast/toast";
import { getAuthCookie } from "../../utils/authCookies";
import { mapFoundationMembershipRowToOption } from "../../utils/orderPartyIdentity";
import {
  createClientProject,
  fetchClientProjectById,
  getDesignRequestBase,
  isAgentDesignRequestRole,
  isBrokerageDesignRequestRole,
  isDraftLikeProjectStatus,
  resolveVendorOrganizationName,
  updateClientProject,
} from "components/designRequestApi";
import PONumber from "components/PONumber";
import "pages/signage/projectandquote-addedit.css";

const PAGE_SIZE = 10;

const DropdownIndicator = (props: any) => (
  <components.DropdownIndicator {...props}>
    <i className="ri-arrow-down-s-line fs-5" style={{ color: "#6c757d" }} />
  </components.DropdownIndicator>
);

const customSelectStyles = {
  dropdownIndicator: (provided: any) => ({
    ...provided,
    display: "flex !important",
    opacity: 1,
    visibility: "visible",
  }),
  indicatorSeparator: (provided: any) => ({ ...provided, display: "none" }),
};

type PartyOption = {
  value: string;
  label: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  organizationId?: string;
  offices?: any[];
  license_type?: string;
};

type WorkflowItem = {
  label: string;
  detail: string;
  iconClass: string;
  tone: "blue" | "purple" | "green" | "orange" | "mint";
};

const createWorkflowItems: WorkflowItem[] = [
  { label: "Request created", detail: "New design request sent to vendor", iconClass: "ri-add-line", tone: "blue" },
  { label: "Design discussions", detail: "You and the vendor collaborate on requirements", iconClass: "ri-message-2-line", tone: "purple" },
  { label: "Design approved", detail: "Final artwork is confirmed", iconClass: "ri-thumb-up-line", tone: "green" },
  { label: "Quoted", detail: "Vendor prepares a quote from the approved design", iconClass: "ri-money-dollar-circle-line", tone: "orange" },
];

function isAllowedArtworkFile(file: File): boolean {
  const ext = file.name.split(".").pop()?.toLowerCase() || "";
  if (["png", "jpg", "jpeg", "pdf", "ai"].includes(ext)) return true;
  const t = file.type || "";
  return t === "image/png" || t === "image/jpeg" || t === "application/pdf" || t === "application/postscript";
}

const buyerOptions = [
  { value: "teammember", label: "Team Member" },
  { value: "agent", label: "Agent" },
];

const DesignRequestAddEdit: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId } = useParams<{ projectId?: string }>();
  const listPath = getDesignRequestBase(location.pathname);
  const isEdit = Boolean(projectId);
  const isAgent = isAgentDesignRequestRole(location.pathname);
  const isBrokerage = isBrokerageDesignRequestRole(location.pathname);
  const isTeammember = location.pathname.includes("/teammember");

  const token = getAuthCookie("sessionToken");
  const userName = getAuthCookie("userName");
  const licenseType = getAuthCookie("licenseType");
  const membershipId = getAuthCookie("membershipId") || "";
  const organizationId = getAuthCookie("organizationId") || "";
  const userId = membershipId;
  const brokerageOrgId = resolveBrokerageOrganizationId();

  const [userInfo, setUserInfo] = useState<any>(null);
  const [loadingProject, setLoadingProject] = useState(isEdit);
  const [isSaving, setIsSaving] = useState(false);
  const [projectStatus, setProjectStatus] = useState("");

  const [selectedVendor, setSelectedVendor] = useState<PartyOption | null>(null);
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [artworkFiles, setArtworkFiles] = useState<File[]>([]);
  const [artworkDragActive, setArtworkDragActive] = useState(false);

  const [userType, setUserType] = useState<"teammember" | "agent" | "">("");
  const [selectedOffice, setSelectedOffice] = useState<PartyOption | null>(null);
  const [selectedTeamMember, setSelectedTeamMember] = useState<PartyOption | null>(null);
  const [selectedTeamMemberOption, setSelectedTeamMemberOption] = useState<PartyOption | null>(null);
  const [selectedOfficeAgent, setSelectedOfficeAgent] = useState<PartyOption | null>(null);
  const [selectedAgent, setSelectedAgent] = useState<PartyOption | null>(null);
  const [selectedAgentOption, setSelectedAgentOption] = useState<PartyOption | null>(null);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [userPhone, setUserPhone] = useState("");

  const normalizeId = (row: any) => String(row?.id ?? row?._id ?? "");

  const mapAgentToOption = (row: any): PartyOption => ({
    value: normalizeId(row),
    label: `${row?.firstName || ""} ${row?.lastName || ""}`.trim() || row?.text || "(no name)",
    firstName: row?.firstName || "",
    lastName: row?.lastName || "",
    email: row?.email || "",
    phone: row?.phone1 || "",
    license_type: String(row?.licenseType ?? row?.license_type ?? "BROKERAGE_AGENT").trim(),
  });

  const canEditCustomer = !isEdit || isDraftLikeProjectStatus(projectStatus);

  const contactFromDetail = (detail: {
    first_name?: string;
    last_name?: string;
    customer_email?: string;
    customer_phone?: string;
  }) => ({
    firstName: detail.first_name || "",
    lastName: detail.last_name || "",
    email: detail.customer_email || "",
    phone: detail.customer_phone || "",
  });

  useEffect(() => {
    const session = sessionLicenseFromAuthCookies();
    setUserInfo(session);
    if (isAgent) {
      setFirstName(session.firstName || "");
      setLastName(session.lastName || "");
      setUserEmail(session.email || "");
      setUserPhone(session.phone1 || session.phone2 || "");
    }
  }, [isAgent]);

  useEffect(() => {
    if (!token || !(membershipId || organizationId)) return;
    if (userType) return;
    if (isEdit) return;
    if (isTeammember && (brokerageOrgId || organizationId)) {
      setUserType("teammember");
    }
  }, [isTeammember, brokerageOrgId, token, membershipId, organizationId, userType, isEdit]);

  useEffect(() => {
    if (!isEdit || !projectId || !token) return;
    let cancelled = false;
    (async () => {
      setLoadingProject(true);
      try {
        const detail = await fetchClientProjectById(token, projectId);
        if (cancelled) return;
        setProjectStatus(detail.status || "");
        setTitle(detail.title || "");
        setMessage(detail.notes || "");
        setPoNumber(String((detail as any)?.po_number ?? (detail as any)?.poNumber ?? "").trim());
        setFirstName(detail.first_name || (detail.customer_name || "").split(" ")[0] || "");
        setLastName(detail.last_name || (detail.customer_name || "").split(" ").slice(1).join(" ") || "");
        setUserEmail(detail.customer_email || "");
        setUserPhone(detail.customer_phone || "");
        const contact = contactFromDetail(detail);
        if (detail.vendor_org_id) {
          const vendorLabel =
            detail.designer_name ||
            (await resolveVendorOrganizationName(token, detail.vendor_org_id)) ||
            "Selected vendor";
          if (cancelled) return;
          setSelectedVendor({
            value: detail.vendor_org_id,
            label: vendorLabel,
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            organizationId: detail.vendor_org_id,
          });
        }
        if (detail.office?._id) {
          setUserType("teammember");
          setSelectedOffice({
            value: detail.office._id,
            label: detail.office.officeName || "Office",
            firstName: detail.office.officeName || "",
            lastName: "",
            email: "",
            phone: "",
          });
        } else if (detail.agent_membership_id) {
          setUserType("agent");
        }
        if (detail.team_member_membership_id) {
          const tmOpt: PartyOption = {
            value: detail.team_member_membership_id,
            label: detail.team_member_name || "Team Member",
            ...contact,
            offices: [],
          };
          setSelectedTeamMember(tmOpt);
          setSelectedTeamMemberOption(tmOpt);
        }
        if (detail.agent_membership_id) {
          const agentOpt: PartyOption = {
            value: detail.agent_membership_id,
            label: detail.agent_name || "Agent",
            ...contact,
          };
          if (detail.office?._id) {
            setSelectedOfficeAgent(agentOpt);
          } else {
            setSelectedAgent(agentOpt);
            setSelectedAgentOption(agentOpt);
          }
        }
      } catch (e) {
        showError(e instanceof Error ? e.message : "Failed to load design request");
      } finally {
        if (!cancelled) setLoadingProject(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isEdit, projectId, token]);

  const loadVendorOptions: LoadOptions<PartyOption, any, { page: number }> = useCallback(
    async (search, _, additional) => {
      if (!token) return { options: [], hasMore: false };
      const page = additional?.page ?? 1;
      try {
        const { results, pagination } = await fetchMarketplaceProfilesSelect2(token, {
          search: search || "",
          page,
          limit: 10,
          category: "signage",
        });
        let options: PartyOption[] = results.map((r: any) => {
          const organizationId = String(r.organization_id || r.organizationId || r.vendor_id || r.id || "").trim();
          return {
            value: organizationId,
            label: r.text || "Vendor",
            firstName: "",
            lastName: "",
            email: "",
            phone: "",
            organizationId,
          };
        }).filter((o) => o.value);
        if (selectedVendor?.value && !options.some((o) => o.value === selectedVendor.value)) {
          options = [selectedVendor, ...options];
        }
        return { options, hasMore: pagination?.more ?? false, additional: { page: page + 1 } };
      } catch {
        return { options: [], hasMore: false };
      }
    },
    [token, selectedVendor]
  );

  const loadOfficeOptions: LoadOptions<PartyOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      if (!brokerageOrgId) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }
      const q: string = (inputValue || "").trim();
      const payload = {
        page,
        limit: PAGE_SIZE,
        search: q,
      };
      try {
        const json = await fetchOfficeSelect(token || "", brokerageOrgId || "", payload);
        const list = json?.results || [];
        const options: PartyOption[] = list.map((office: any) => ({
          value: office.id || office._id,
          label: office.officeName || "---",
          firstName: office.officeName || "",
          lastName: "",
          email: "",
          phone: office.officeAddress?.[0]?.phone || "",
        }));
        return {
          options,
          hasMore: json?.pagination?.more ?? false,
          additional: { page: page + 1 },
        };
      } catch {
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [brokerageOrgId, token]
  );

  const loadTeamMemberOptions: LoadOptions<PartyOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      const officeId = selectedOffice?.value;
      if (!officeId) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }
      const q: string = (inputValue || "").trim();
      const payload = {
        page,
        limit: PAGE_SIZE,
        search: q,
      };
      try {
        const json = await fetchOfficeTeamMembersSelect(token!, officeId, payload);
        const list = json?.results || [];
        const options: PartyOption[] = list.map((u: any) => ({
          value: String(u?.id ?? u?._id ?? ""),
          label: `${u?.firstName || ""} ${u?.lastName || ""}`.trim() || "---",
          firstName: u?.firstName || "",
          lastName: u?.lastName || "",
          email: u?.email || "",
          phone: u?.phone1 || "",
          offices: [],
          license_type: String(
            u?.licenseType ?? u?.license_type ?? "BROKERAGE_TEAM_MEMBER"
          ).trim(),
        }));
        return {
          options,
          hasMore: json?.pagination?.more ?? false,
          additional: { page: page + 1 },
        };
      } catch {
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [selectedOffice?.value, token]
  );

  const loadOfficeAgentOptions: LoadOptions<PartyOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      const officeId = selectedOffice?.value;
      if (!officeId) {
        return { options: [], hasMore: false, additional: { page: 1 } };
      }
      const q: string = (inputValue || "").trim();
      const payload = {
        page,
        limit: PAGE_SIZE,
        search: q,
      };
      try {
        const json = await fetchBrokerageAgentsByOfficeIdsSelect2(token!, officeId, payload);
        const list = json?.results || [];
        const options: PartyOption[] = list.map((u: any) => ({
          ...mapFoundationMembershipRowToOption(u, { officeId }),
          license_type: String(
            u?.local_license_type ?? u?.licenseType ?? "BROKERAGE_AGENT"
          ),
        }));
        return {
          options,
          hasMore: json?.pagination?.more ?? false,
          additional: { page: page + 1 },
        };
      } catch {
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [selectedOffice?.value, token]
  );

  const loadAgentOptions: LoadOptions<PartyOption, any, { page: number }> = useCallback(
    async (inputValue, _loadedOptions, additional) => {
      const page = additional?.page ?? 1;
      const params = new URLSearchParams({
        licenseType: "BROKERAGE_AGENT",
        brokerageFirmID: brokerageOrgId,
        page: String(page),
        limit: String(PAGE_SIZE),
      });
      const search = (inputValue || "").trim();
      if (search) params.set("search", search);
      const url = `${process.env.REACT_APP_API_URL}/organization-profiles/search?${params.toString()}`;
      try {
        const response = await fetch(url, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });
        if (!response.ok) {
          return { options: [], hasMore: false, additional: { page } };
        }
        const responseData = await response.json();
        const results = Array.isArray(responseData) ? responseData : responseData.results || [];
        const hasMore = Array.isArray(responseData)
          ? results.length === PAGE_SIZE
          : responseData.pagination?.more || false;
        const options: PartyOption[] = results.map(mapAgentToOption);
        return {
          options,
          hasMore,
          additional: { page: page + 1 },
        };
      } catch {
        return { options: [], hasMore: false, additional: { page } };
      }
    },
    [brokerageOrgId, token, userId, licenseType, userName]
  );

  const resetPartyContact = () => {
    setFirstName("");
    setLastName("");
    setUserEmail("");
    setUserPhone("");
  };

  const applyPerson = (opt: PartyOption | null) => {
    setFirstName(opt?.firstName || "");
    setLastName(opt?.lastName || "");
    setUserEmail(opt?.email || "");
    setUserPhone(opt?.phone || "");
  };

  const handleOfficeSelection = (office: PartyOption | null) => {
    setSelectedTeamMember(null);
    setSelectedTeamMemberOption(null);
    setSelectedOfficeAgent(null);
    setSelectedAgent(null);
    setSelectedAgentOption(null);
    resetPartyContact();
    setSelectedOffice(office);
  };

  const mergeArtworkFiles = (incoming: FileList | File[]) => {
    const add: File[] = [];
    Array.from(incoming).forEach((f) => {
      if (!isAllowedArtworkFile(f)) {
        showError(`${f.name} is not an allowed file type (PNG, JPG, PDF, AI).`);
        return;
      }
      add.push(f);
    });
    if (add.length) setArtworkFiles((prev) => [...prev, ...add]);
  };

  const omitEmpty = (obj: Record<string, unknown>) => {
    const out: Record<string, unknown> = {};
    Object.entries(obj).forEach(([k, v]) => {
      if (v == null) return;
      if (typeof v === "string" && !v.trim()) return;
      out[k] = v;
    });
    return out;
  };

  const buildPartyPayload = () => {
    const customerName = `${firstName} ${lastName}`.trim();
    const brokerageOrgIdForPayload = String(
      brokerageOrgId ||
        userInfo?.organization_id ||
        userInfo?.firmDetails?.id ||
        organizationId ||
        ""
    ).trim();

    const payload: Record<string, unknown> = {
      title: title.trim(),
      notes: message.trim() || undefined,
      po_number: poNumber.trim() || undefined,
      first_name: firstName.trim() || undefined,
      last_name: lastName.trim() || undefined,
      customer_name: customerName || undefined,
      customer_email: userEmail.trim() || undefined,
      customer_phone: userPhone.trim() || undefined,
      license_type: userInfo?.licenseType || licenseType || undefined,
      brokerage_firm_organization_id: brokerageOrgIdForPayload || undefined,
    };

    if (isBrokerage && selectedOffice?.value) {
      payload.office = { _id: selectedOffice.value, officeName: selectedOffice.label || "Office" };
    }
    if (isBrokerage && userType === "agent" && selectedAgent?.value) {
      payload.agent_membership_id = selectedAgent.value;
      payload.agent_name = selectedAgent.label;
    }
    if (isBrokerage && userType === "teammember" && selectedTeamMember?.value) {
      payload.team_member_membership_id = selectedTeamMember.value;
      payload.team_member_name = selectedTeamMember.label;
    }
    if (isBrokerage && userType === "teammember" && selectedOfficeAgent?.value) {
      payload.agent_membership_id = selectedOfficeAgent.value;
      payload.agent_name = selectedOfficeAgent.label;
    }
    if (isAgent) {
      payload.agent_membership_id = membershipId || userId;
      payload.agent_name = `${firstName} ${lastName}`.trim() || userInfo?.firstName;
    }
    return omitEmpty(payload);
  };

  const buildEditPayload = () => {
    const payload = buildPartyPayload();
    if (!canEditCustomer) {
      return omitEmpty({
        title: title.trim(),
        notes: message.trim() || undefined,
      });
    }
    delete payload.agent_membership_id;
    delete payload.agent_name;
    delete payload.team_member_membership_id;
    delete payload.team_member_name;
    delete payload.brokerage_firm_organization_id;
    return payload;
  };

  const handleSubmit = async () => {
    if (isSaving) return;
    const vendorOrgId = selectedVendor?.organizationId || selectedVendor?.value || "";
    if (!vendorOrgId) {
      showError("Select a vendor to continue");
      return;
    }
    if (!title.trim()) {
      showError("Enter a design title");
      return;
    }
    if (!isEdit && artworkFiles.length === 0) {
      showError("Add at least one artwork file");
      return;
    }
    if (!isEdit || canEditCustomer) {
      if (!userEmail.trim()) {
        showError("Email is required");
        return;
      }
      if (isBrokerage) {
        if (!userType) {
          showError("Select a buyer type");
          return;
        }
        if (userType === "agent" && !selectedAgent?.value) {
          showError("Select an agent to continue");
          return;
        }
        if (userType === "teammember" && !selectedOffice?.value) {
          showError("Select an office to continue");
          return;
        }
        if (userType === "teammember" && !selectedTeamMember?.value && !selectedOfficeAgent?.value) {
          showError("Select a team member to continue");
          return;
        }
      }
    }

    if (!token) {
      showError("Your session expired. Sign in again.");
      return;
    }

    setIsSaving(true);
    try {
      if (isEdit && projectId) {
        await updateClientProject(token, projectId, buildEditPayload());
        showSuccess("Design request updated");
        navigate(`${listPath}/view/${encodeURIComponent(projectId)}`);
      } else {
        const created = await createClientProject(token, { ...buildPartyPayload(), vendor_org_id: vendorOrgId }, artworkFiles);
        showSuccess("Design request created");
        navigate(`${listPath}/view/${encodeURIComponent(created.projectId)}`);
      }
    } catch (e) {
      showError(e instanceof Error ? e.message : "Failed to save design request");
    } finally {
      setIsSaving(false);
    }
  };

  const workflowUi = useMemo(() => createWorkflowItems, []);

  if (loadingProject) {
    return <div className="pq-create-page p-5">Loading design request…</div>;
  }

  return (
    <div className="pq-create-page">
      <div className="pq-create-header">
        <div>
          <h1 className="pq-create-title">{isEdit ? "Edit Design Request" : "Create a Design Request"}</h1>
          <p className="pq-create-subtitle">
            {isEdit ? "Update request details and requirements" : "Start a new design conversation with a vendor"}
          </p>
        </div>
        <button className="pq-top-cancel-btn" type="button" onClick={() => navigate(listPath)}>
          Cancel
        </button>
      </div>

      <div className="pq-create-layout">
        <div className="pq-create-left">
          <div className="pq-create-card">
            <div className="pq-create-card-body">
              <div className="pq-form-group">
                <label className="pq-form-label">
                  Select Vendor <span className="text-danger">*</span>
                </label>
                <AsyncPaginate
                  key={`vendor-${selectedVendor?.value || "new"}-${selectedVendor?.label || ""}`}
                  classNamePrefix="rs"
                  placeholder="Search vendors…"
                  value={selectedVendor}
                  loadOptions={loadVendorOptions}
                  onChange={(opt) => setSelectedVendor(opt as PartyOption | null)}
                  additional={{ page: 1 }}
                  defaultOptions
                  debounceTimeout={300}
                  isClearable={!isEdit}
                  isDisabled={isEdit}
                  components={{ DropdownIndicator }}
                  styles={customSelectStyles}
                />
              </div>
            </div>
          </div>

          <div className="row col-lg-12 p-3 card-body shadow-lg pb-4 mb-4 ms-0">
            <div className="mb-3">
              <SectionHeader title="Customer Information" icon="ri-user-3-line" color="#155dfc" size="md" divider />
              {isEdit && !canEditCustomer ? (
                <div className="text-muted small mt-1">Customer information can be changed only while the request is in draft / in progress.</div>
              ) : null}
            </div>

            {isAgent ? (
              <>
                <div className="col-lg-3 mb-3">
                  <label className="form-label table-headers">First Name <span className="text-danger">*</span></label>
                  <input className="form-control table-cells" value={firstName} readOnly />
                </div>
                <div className="col-lg-3 mb-3">
                  <label className="form-label table-headers">Last Name <span className="text-danger">*</span></label>
                  <input className="form-control table-cells" value={lastName} readOnly />
                </div>
                <div className="col-lg-3 mb-3">
                  <label className="form-label table-headers">Email <span className="text-danger">*</span></label>
                  <input className="form-control table-cells" value={userEmail} onChange={(e) => setUserEmail(e.target.value)} readOnly={!canEditCustomer} />
                </div>
                <div className="col-lg-3 mb-3">
                  <label className="form-label table-headers">Phone <span className="text-danger">*</span></label>
                  <input className="form-control table-cells" value={userPhone} onChange={(e) => setUserPhone(e.target.value)} readOnly={!canEditCustomer} />
                </div>
              </>
            ) : (
              <>
                <div className="col-lg-3 mb-3">
                  <label htmlFor="userType" className="form-label table-cells">
                    Buyer Type <span className="text-danger">*</span>
                  </label>
                  <Select
                    id="userType"
                    name="userType"
                    options={buyerOptions}
                    value={buyerOptions.find((option) => option.value === userType) || null}
                    isDisabled={!canEditCustomer}
                    onChange={(selectedOption) => {
                      setSelectedTeamMember(null);
                      setSelectedTeamMemberOption(null);
                      setSelectedAgentOption(null);
                      setSelectedOffice(null);
                      setSelectedOfficeAgent(null);
                      setSelectedAgent(null);
                      resetPartyContact();
                      setUserType((selectedOption?.value as "teammember" | "agent") || "");
                    }}
                    components={{ DropdownIndicator }}
                    styles={customSelectStyles}
                    classNamePrefix="rs"
                  />
                </div>
                {userType === "teammember" ? (
                  <>
                    <div className="col-lg-3 mb-3">
                      <label htmlFor="officeSelection" className="table-cells">
                        Select Office <span className="text-danger">*</span>
                      </label>
                      <AsyncPaginate
                        classNamePrefix="rs"
                        placeholder="Select office…"
                        value={selectedOffice}
                        loadOptions={loadOfficeOptions}
                        isDisabled={!canEditCustomer}
                        onChange={(selected) => {
                          if (selected?.value) {
                            handleOfficeSelection(selected as PartyOption);
                          } else {
                            setSelectedOffice(null);
                            setSelectedTeamMember(null);
                            setSelectedTeamMemberOption(null);
                            setSelectedOfficeAgent(null);
                            setSelectedAgent(null);
                            setSelectedAgentOption(null);
                            resetPartyContact();
                          }
                        }}
                        additional={{ page: 1 }}
                        defaultOptions
                        debounceTimeout={300}
                        isClearable
                        components={{ DropdownIndicator }}
                        styles={customSelectStyles}
                      />
                    </div>
                    {selectedOffice && (
                      <div className="col-lg-3 mb-3">
                        <label htmlFor="teamMemberId" className="table-cells">
                          Select Team Member <span className="text-danger">*</span>
                        </label>
                        <AsyncPaginate
                          key={`office-${selectedOffice?.value || "no-office"}`}
                          classNamePrefix="rs"
                          placeholder="Select team member…"
                          value={selectedTeamMemberOption ? {
                            value: selectedTeamMemberOption.value,
                            label: selectedTeamMemberOption.label,
                            firstName: selectedTeamMemberOption.firstName,
                            lastName: selectedTeamMemberOption.lastName,
                            email: selectedTeamMemberOption.email,
                            phone: selectedTeamMemberOption.phone,
                            offices: selectedTeamMemberOption.offices,
                          } : null}
                          loadOptions={loadTeamMemberOptions}
                          isDisabled={!canEditCustomer}
                          onChange={(selected) => {
                            if (selected) {
                              setSelectedOfficeAgent(null);
                              setSelectedAgent(null);
                              setSelectedAgentOption(null);
                              setSelectedTeamMember(selected as PartyOption);
                              setSelectedTeamMemberOption(selected as PartyOption);
                              applyPerson(selected as PartyOption);
                            } else {
                              setSelectedTeamMember(null);
                              setSelectedTeamMemberOption(null);
                              setSelectedOfficeAgent(null);
                              setSelectedAgent(null);
                              setSelectedAgentOption(null);
                              resetPartyContact();
                            }
                          }}
                          additional={{ page: 1 }}
                          defaultOptions
                          debounceTimeout={300}
                          isClearable
                          components={{ DropdownIndicator }}
                          styles={customSelectStyles}
                        />
                      </div>
                    )}
                    {selectedOffice && (selectedTeamMemberOption || selectedOfficeAgent) && (
                      <div className="col-lg-3 mb-3">
                        <label htmlFor="officeAgentSelection" className="table-cells">
                          Select Agent
                        </label>
                        <AsyncPaginate
                          key={`office-agents-${selectedOffice?.value || "no-office"}`}
                          classNamePrefix="rs"
                          placeholder="Select agent…"
                          value={selectedOfficeAgent ? {
                            value: selectedOfficeAgent.value,
                            label: selectedOfficeAgent.label,
                            firstName: selectedOfficeAgent.firstName,
                            lastName: selectedOfficeAgent.lastName,
                            email: selectedOfficeAgent.email,
                            phone: selectedOfficeAgent.phone,
                          } : null}
                          loadOptions={loadOfficeAgentOptions}
                          isDisabled={!canEditCustomer}
                          onChange={(selected) => {
                            if (selected) {
                              setSelectedOfficeAgent(selected as PartyOption);
                              setSelectedAgent(selected as PartyOption);
                              applyPerson(selected as PartyOption);
                            } else {
                              setSelectedOfficeAgent(null);
                              setSelectedAgent(null);
                              if (selectedTeamMember) applyPerson(selectedTeamMember);
                              else resetPartyContact();
                            }
                          }}
                          additional={{ page: 1 }}
                          defaultOptions
                          debounceTimeout={300}
                          isClearable
                          components={{ DropdownIndicator }}
                          styles={customSelectStyles}
                        />
                      </div>
                    )}
                  </>
                ) : (
                  userType === "agent" && (
                    <div className="col-lg-3 mb-3">
                      <label htmlFor="agentSelection" className="table-cells">
                        Select Agent <span className="text-danger">*</span>
                      </label>
                      <AsyncPaginate
                        classNamePrefix="rs"
                        placeholder="Search agents..."
                        value={selectedAgentOption ? {
                          value: selectedAgentOption.value,
                          label: selectedAgentOption.label,
                          firstName: selectedAgentOption.firstName,
                          lastName: selectedAgentOption.lastName,
                          email: selectedAgentOption.email,
                          phone: selectedAgentOption.phone,
                        } : null}
                        loadOptions={loadAgentOptions}
                        isDisabled={!canEditCustomer}
                        onChange={(selected) => {
                          if (selected) {
                            setSelectedAgent(selected as PartyOption);
                            setSelectedAgentOption(selected as PartyOption);
                            applyPerson(selected as PartyOption);
                          } else {
                            setSelectedAgent(null);
                            setSelectedAgentOption(null);
                            resetPartyContact();
                          }
                        }}
                        additional={{ page: 1 }}
                        defaultOptions
                        debounceTimeout={300}
                        isClearable
                        components={{ DropdownIndicator }}
                        styles={customSelectStyles}
                      />
                    </div>
                  )
                )}

                <div className="col-lg-3 mb-3">
                  <label htmlFor="userEmail" className="form-label table-headers">
                    Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control table-cells"
                    id="userEmail"
                    name="userEmail"
                    placeholder="Enter email"
                    value={userEmail}
                    onChange={(e) => setUserEmail(e.target.value)}
                    readOnly={!canEditCustomer}
                  />
                </div>
                <div className="col-lg-3 mb-3">
                  <label htmlFor="userPhone" className="form-label table-headers">
                    Phone <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control table-cells"
                    id="userPhone"
                    name="userPhone"
                    placeholder="Enter phone"
                    value={userPhone}
                    onChange={(e) => setUserPhone(e.target.value)}
                    readOnly={!canEditCustomer}
                  />
                </div>
              </>
            )}
          </div>

          <div className="pq-create-card">
            <div className="pq-create-card-body">
              <div className="pq-form-group">
                <label className="pq-form-label">
                  Design Title <span className="text-danger">*</span>
                </label>
                <input
                  className="pq-text-input"
                  placeholder="e.g. 123 Main St Yard Sign Rider"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                />
              </div>

              {!isEdit ? (
                <div className="pq-form-group">
                  <label className="pq-form-label">
                    Upload Initial Artwork <span className="text-danger">*</span>
                  </label>
                  <div
                    className={`pq-upload-box ${artworkDragActive ? "pq-upload-box--drag" : ""}`}
                    onDragOver={(e) => {
                      e.preventDefault();
                      setArtworkDragActive(true);
                    }}
                    onDragLeave={() => setArtworkDragActive(false)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setArtworkDragActive(false);
                      if (e.dataTransfer.files?.length) mergeArtworkFiles(e.dataTransfer.files);
                    }}
                  >
                    <i className="ri-upload-cloud-2-line pq-upload-box-icon" />
                    <div className="pq-upload-box-title">Drop files here or browse</div>
                    <div className="pq-upload-box-note">PNG, JPG, PDF, AI · at least one required</div>
                    <label className="pq-upload-box-btn">
                      Browse Files
                      <input
                        type="file"
                        multiple
                        hidden
                        accept=".png,.jpg,.jpeg,.pdf,.ai"
                        onChange={(e) => {
                          if (e.target.files?.length) mergeArtworkFiles(e.target.files);
                          e.currentTarget.value = "";
                        }}
                      />
                    </label>
                  </div>
                  {artworkFiles.length ? (
                    <div className="pq-uploaded-files">
                      {artworkFiles.map((file, idx) => (
                        <div className="pq-uploaded-file" key={`${file.name}-${idx}`}>
                          <div className="pq-uploaded-file-left">
                            <i className="ri-file-2-line pq-uploaded-file-icon" />
                            <span className="pq-uploaded-file-name">{file.name}</span>
                          </div>
                          <button
                            type="button"
                            className="pq-uploaded-file-remove"
                            onClick={() => setArtworkFiles((prev) => prev.filter((_, i) => i !== idx))}
                          >
                            <i className="ri-close-line" />
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : null}
                </div>
              ) : null}

              <div className="pq-form-group">
                <label className="pq-form-label">Message to Vendor</label>
                <textarea
                  className="pq-textarea-input"
                  placeholder="Describe what you need — colors, sizes, text, headshot requirements..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
              </div>

              <div className="pq-bottom-actions mb-3">
                <button className="pq-footer-cancel-btn" type="button" onClick={() => navigate(listPath)}>
                  Cancel
                </button>
                <button className="pq-footer-primary-btn" type="button" onClick={() => void handleSubmit()} disabled={isSaving}>
                  {isSaving ? (isEdit ? "Saving…" : "Creating…") : isEdit ? "Save Changes" : "Create Request"}
                </button>
              </div>
            </div>
          </div>
        </div>

        <div className="pq-create-right">
          <PONumber
            mode="create"
            className="mb-3"
            value={poNumber}
            onChange={setPoNumber}
          />
          <div className="pq-workflow-card">
            <div className="pq-workflow-card-head">Request Workflow</div>
            <div className="pq-workflow-card-body">
              <div className="pq-workflow-list-wrap">
                {workflowUi.map((item, index) => (
                  <div className="pq-workflow-row" key={item.label}>
                    <div className="pq-workflow-left">
                      <div className={`pq-workflow-icon ${item.tone}`}>
                        <i className={item.iconClass} />
                      </div>
                      {index !== workflowUi.length - 1 && <div className="pq-workflow-line" />}
                    </div>
                    <div className="pq-workflow-content">
                      <div className="pq-workflow-title">{item.label}</div>
                      <div className="pq-workflow-detail">{item.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DesignRequestAddEdit;
